import React, { useState, useContext } from 'react';
import RoleTable from '../components/Roles/RoleTable';
import RoleFormModal from '../components/Roles/RoleFormModal';
import { RoleContext } from '../components/Roles/RoleContext';

const RolePage = () => {
  const { roles, addRole, editRole, deleteRole } = useContext(RoleContext);

  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [loading, setLoading] = useState(false); // For loading state
  const [successMessage, setSuccessMessage] = useState(''); // For success message

  // Handle add role
  const handleAddRole = () => {
    setIsEditing(false);
    setEditingRole(null);
    setModalVisible(true);
  };

  // Handle edit role
  const handleEditRole = (role) => {
    setIsEditing(true);
    setEditingRole(role);
    setModalVisible(true);
  };

  // Handle save role (add or edit)
  const handleSaveRole = async (roleData) => {
    setLoading(true); // Set loading true when making API calls
    try {
      if (isEditing) {
        await editRole(editingRole.id, { ...editingRole, ...roleData });
        setSuccessMessage('Role updated successfully!');
      } else {
        await addRole(roleData);
        setSuccessMessage('Role added successfully!');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      setSuccessMessage('Error occurred. Please try again!');
    } finally {
      setLoading(false); // Set loading false after the API call
      setModalVisible(false); // Close the modal

      // Timeout to clear the success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  };

  // Handle cancel (close the modal)
  const handleCancel = () => {
    setModalVisible(false);
  };

  // Handle delete role
  const handleDeleteRole = async (id) => {
    setLoading(true); // Set loading true when deleting
    try {
      await deleteRole(id);
      setSuccessMessage('Role deleted successfully!');
    } catch (error) {
      console.error('Error deleting role:', error);
      setSuccessMessage('Error occurred. Please try again!');
    } finally {
      setLoading(false); // Set loading false after the deletion
      // Timeout to clear the success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  };

  return (
    <div className="container py-4">
      <div className="card shadow-lg p-4 border-0">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-bold text-primary">Role Management</h3>
          <button
            className="btn btn-outline-primary btn-lg"
            onClick={handleAddRole}
            disabled={loading}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-plus-circle me-2"></i>
            )}
            Add Role
          </button>
        </div>

        {successMessage && (
          <div className="alert alert-success" role="alert">
            {successMessage}
          </div>
        )}

        <RoleTable onEdit={handleEditRole} onDelete={handleDeleteRole} roles={roles} />

        {/* Loader when loading */}
        {loading && (
          <div className="d-flex justify-content-center mt-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>

      {/* Modal for adding/editing role */}
      {modalVisible && (
        <RoleFormModal
          onSubmit={handleSaveRole}
          onCancel={handleCancel}
          role={editingRole}
          isEditing={isEditing}
        />
      )}
    </div>
  );
};

export default RolePage;
